import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useLanguage } from '../../../components/context/LanguageContext'
import { Button, Card, Col, Row, Table, Tabs, message, Modal } from 'antd'
import { Content } from 'antd/es/layout/layout'
import {
  ReloadOutlined,
  DownloadOutlined,
  OpenAIOutlined,
  CheckOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  UploadOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { apiRequest } from '../../../helpers'

const url = 'agents/recruiters/resumes/'
const item = 'Resume'
const items = 'Resumes'

const AgentRecruiterDetails = ({ agent, refreshData }) => {
  const languageContext = useLanguage()
  const [running, setRunning] = useState(false)
  const [canUpload, setCanUpload] = useState(false)
  const [canDelete, setCanDelete] = useState(false)
  const [canProcessMultiple, setProcessMultiple] = useState(false)
  const [resumes, setResumes] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [uploadMessages, setUploadMessages] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [showDeleteModal, setDeleteModal] = useState(false)
  const [allSelected, setAllSelected] = useState(false)

  const containerRef = useRef(null)
  const fileInputRef = useRef(null)

  const fetchResumes = useCallback(async () => {
    console.log('agent', agent)
    // fetch agent storage details, if storageId is set
    if (agent.storageId) {
      const result = await apiRequest(process.env.REACT_APP_API + `connector/storage/${agent.storageId}`, 'GET')
      if (!result.ok) {
        message.error('Failed to fetch storage details')
        return
      }
      agent.storage = result.data
      setCanUpload(agent.storage?.canUpload)
      setCanDelete(agent.storage?.canDelete)
      setProcessMultiple(agent.storage?.canProcessMultiple)
    }
    // fetch resumes list
    const result = await apiRequest(process.env.REACT_APP_API + `agents/recruiters/${agent.key}/resumes`, 'GET')
    if (!result.ok) {
      message.error('Failed to fetch resumes')
      return
    }
    setResumes(
      result.data.map((item, index) => ({
        ...item,
        details: JSON.parse(item.details),
        key: index,
      }))
    )
  }, [agent])

  useEffect(() => {
    fetchResumes()
  }, [fetchResumes])

  const run = async () => {
    setRunning(true)
    try {
      await apiRequest(process.env.REACT_APP_API + `agents/recruiters/${agent.key}/execute`, 'POST', { debug: true })
      await fetchResumes()
    } catch (error) {
      message.error('Failed to execute agent')
    }
    setRunning(false)
  }

  const onResumeDownload = resume => {
    window.open(process.env.REACT_APP_API + `agents/recruiters/${agent.key}/resumes/${resume.id}/download`, '_blank')
  }

  const onResumePreview = async resume => {
    window.open(process.env.REACT_APP_API + `agents/recruiters/${agent.key}/resumes/${resume.id}/preview`, '_blank')
  }

  const onResumeProcess = async resume => {
    setRunning(true)
    try {
      await apiRequest(process.env.REACT_APP_API + `agents/recruiters/${agent.key}/resumes/${resume.id}/process`, 'POST', { debug: true })
      await fetchResumes()
    } catch (error) {
      message.error('Failed to process resume')
    }
    setRunning(false)
  }

  const sortString = (a, b) => {
    if (!a && !b) return 0
    if (a && !b) return 1
    if (!a && b) return -1
    return a.localeCompare(b)
  }

  const columns = [
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      sorter: (a, b) => sortString(a.filename, b.filename),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => {
        const statusMap = {
          0: 'pending',
          1: 'processing',
          2: 'processed',
          3: 'error',
        }
        return statusMap[status] || ''
      },
      sorter: (a, b) => a.status - b.status,
    },
    {
      title: 'Match',
      dataIndex: 'match',
      key: 'match',
      render: (match, record) => (record.status === 2 ? match ? <CheckOutlined /> : <CloseOutlined /> : ''),
      sorter: (a, b) => (a.match === b.match ? 0 : a.match ? -1 : 1),
    },
    {
      title: 'Date',
      dataIndex: ['createdAt'],
      key: 'createdAt',
      render: createdAt => new Date(createdAt).toLocaleDateString(),
    },
    {
      title: 'First Name',
      dataIndex: ['details', 'firstName'],
      key: 'firstName',
      sorter: (a, b) => sortString(a.details?.firstName, b.details?.firstName),
    },
    {
      title: 'Last Name',
      dataIndex: ['details', 'lastName'],
      key: 'lastName',
      sorter: (a, b) => sortString(a.details?.lastName, b.details?.lastName),
    },
    {
      title: 'Gender',
      dataIndex: ['details', 'gender'],
      key: 'gender',
      sorter: (a, b) => sortString(a.details?.gender, b.details?.gender),
    },
    {
      title: 'Exp Yrs',
      dataIndex: ['details', 'yearsOfExperience'],
      key: 'yearsOfExperience',
      sorter: (a, b) => (a.details?.yearsOfExperience || 0) - (b.details?.yearsOfExperience || 0),
    },
    // {
    //   title: 'Age',
    //   dataIndex: ['details', 'birthDate'],
    //   key: 'birthDate',
    //   render: (birthDate, record) => {
    //     const birthYear = new Date(record?.details?.birthDate).getFullYear()
    //     const currentYear = new Date().getFullYear()
    //     return currentYear - birthYear
    //   },
    // },
    {
      title: 'Native Lang',
      dataIndex: ['details', 'nativeLanguage'],
      key: 'nativeLanguage',
      sorter: (a, b) => sortString(a.details?.nativeLanguage, b.details?.nativeLanguage),
    },
    {
      title: 'Score',
      dataIndex: ['details', 'matchingPercentage'],
      key: 'matchingPercentage',
      render: matchingPercentage => (matchingPercentage ? `${matchingPercentage}%` : ''),
      sorter: (a, b) => (a.details?.matchingPercentage || 0) - (b.details?.matchingPercentage || 0),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <Button type="text" icon={<EyeOutlined />} onClick={() => onResumePreview(record)} />
          <Button type="text" icon={<DownloadOutlined />} onClick={() => onResumeDownload(record)} />
          {record.status !== 2 && <Button type="text" icon={<OpenAIOutlined />} onClick={() => onResumeProcess(record)} disabled={running} />}
        </div>
      ),
    },
  ]

  const uploadFile = file => {
    const formData = new FormData()
    formData.append('resumes', file)

    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', e => {
      if (e.lengthComputable) {
        const percentComplete = Math.round((e.loaded / e.total) * 100)
        setUploadProgress(prevProgress => ({
          ...prevProgress,
          [file.name]: percentComplete,
        }))
      }
    })

    xhr.onload = async () => {
      if (xhr.status === 200 || xhr.status === 201) {
        setUploadMessages(prevMessages => [...prevMessages, `${file.name} uploaded successfully.`])
        message.success(`${file.name} uploaded successfully.`)
        await fetchResumes()
      } else {
        setUploadMessages(prevMessages => [...prevMessages, `Error uploading ${file.name}.`])
        message.error(`Error uploading ${file.name}.`)
      }
      setUploadProgress(prevProgress => {
        const newProgress = { ...prevProgress }
        delete newProgress[file.name]
        return newProgress
      })
    }

    xhr.onerror = () => {
      setUploadMessages(prevMessages => [...prevMessages, `Upload failed for ${file.name}.`])
      message.error(`Upload failed for ${file.name}.`)
      setUploadProgress(prevProgress => {
        const newProgress = { ...prevProgress }
        delete newProgress[file.name]
        return newProgress
      })
    }

    xhr.open('POST', process.env.REACT_APP_API + `agents/recruiters/${agent.key}/resumes/new`)
    xhr.setRequestHeader('recruiter-id', agent.id)
    xhr.setRequestHeader('tenant-id', agent.tenantId)
    xhr.withCredentials = true
    xhr.send(formData)
  }

  const handleFileInputChange = e => {
    const files = Array.from(e.target.files)
    files.forEach(uploadFile)
  }

  const handleDragEnter = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    if (!containerRef.current.contains(e.relatedTarget)) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    e => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const files = Array.from(e.dataTransfer.files)
      files.forEach(uploadFile)
    },
    [agent]
  )

  const onSelectChange = newSelectedRowKeys => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const onSelectAllChange = () => {
    if (allSelected) {
      setSelectedRowKeys([])
    } else {
      setSelectedRowKeys(resumes.map(resume => resume.key))
    }
    setAllSelected(!allSelected)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    onSelectAll: (selected, selectedRows, changeRows) => {
      if (selected) {
        setSelectedRowKeys([...new Set([...selectedRowKeys, ...changeRows.map(row => row.key)])])
      } else {
        setSelectedRowKeys(selectedRowKeys.filter(key => !changeRows.some(row => row.key === key)))
      }
    },
  }
  const hasSelected = selectedRowKeys.length > 0

  const deleteResumes = async () => {
    if (hasSelected) {
      const selectedResumes = resumes.filter(resume => selectedRowKeys.includes(resume.key)).map(resume => resume.id)
      try {
        await apiRequest(process.env.REACT_APP_API + `agents/recruiters/${agent.key}/resumes/delete`, 'POST', { ids: selectedResumes })
        await fetchResumes()
        setSelectedRowKeys([])
      } catch (error) {
        message.error('Failed to delete resumes')
      }
    }
    setDeleteModal(false)
  }

  const processMultipleResumes = async () => {
    setRunning(true)
    if (hasSelected) {
      const selectedResumes = resumes.filter(resume => selectedRowKeys.includes(resume.key)).map(resume => resume.id)
      try {
        await apiRequest(process.env.REACT_APP_API + `agents/recruiters/${agent.key}/resumes/process`, 'POST', { ids: selectedResumes })
        await fetchResumes()
        setSelectedRowKeys([])
      } catch (error) {
        message.error('Failed to delete resumes')
      }
    }
    setRunning(false)
  }

  const downloadSummary = record => {
    const csvContent = Object.entries(record.details)
      .map(
        ([key, value]) =>
          `${key},${typeof value === 'string' && (value.startsWith('https://') || value.startsWith('http://')) ? `"${value}"` : value}`
      )
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `${record.filename}_summary.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadMultipleSummary = () => {
    const selectedResumes = resumes.filter(resume => selectedRowKeys.includes(resume.key) && resume.status === 2)
    if (selectedResumes.length === 0) return

    const headers = Object.keys(selectedResumes[0].details)
    const rows = selectedResumes.map(resume =>
      headers.map(header => {
        const value = resume.details[header]
        return Array.isArray(value) ? value.join('\n') : value
      })
    )
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n')
    const bom = '\uFEFF'
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `resumes_summary.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Tabs className="mt-3" defaultActiveKey="2" type="card">
      <Tabs.TabPane tab={languageContext.t('Resumes')} key="2">
        <Content className="p-3">
          {showDeleteModal && (
            <Modal
              title={`${languageContext.t('Delete')} ${selectedRowKeys.length} ${languageContext.t('resumes')}`}
              open={showDeleteModal}
              onCancel={() => setDeleteModal(false)}
              onOk={() => deleteResumes()}
              className="dyn-form-modal"
            >
              <p>{languageContext.t('DeleteMessage')}</p>
            </Modal>
          )}
          <Row>
            {!agent.active && <Col>{languageContext.t('Agent is not active')}</Col>}
            {agent.active && (
              <>
                <Col sm={12}>
                  <Button className="dyn-btn-primary" onClick={run} disabled={running || agent.status !== 'active'}>
                    {languageContext.t(running ? 'Retrieving' : 'Retrieve')} {languageContext.t('resumes')} {languageContext.t('now')}
                  </Button>
                  {canDelete && hasSelected && (
                    <>
                      <Button className="dyn-btn-secondary ml-2 mr-2" style={{ marginRight: '10px' }} onClick={() => setDeleteModal(true)}>
                        {languageContext.t('Delete')} {selectedRowKeys.length} {languageContext.t('resumes')}
                      </Button>
                    </>
                  )}
                  {hasSelected && (
                    <>
                      <Button className="dyn-btn-primary ml-2 mr-2" onClick={() => processMultipleResumes(true)}>
                        {languageContext.t('Process')} {selectedRowKeys.length} {languageContext.t('resumes')}
                      </Button>
                      <Button className="dyn-btn-primary ml-2 mr-2" onClick={() => downloadMultipleSummary()}>
                        {languageContext.t('Download Summary')} {selectedRowKeys.length} {languageContext.t('resumes')}
                      </Button>
                    </>
                  )}
                  {running && (
                    <span className="ml-2">
                      <ReloadOutlined spin />
                    </span>
                  )}
                </Col>
                <Col sm={12} style={{ textAlign: 'right' }}>
                  {canUpload && (
                    <>
                      <Button type="text" icon={<UploadOutlined />} onClick={() => fileInputRef.current.click()} className="mr-3" />
                      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileInputChange} multiple />
                    </>
                  )}
                  <Button type="text" icon={<ReloadOutlined />} onClick={fetchResumes} />
                </Col>
              </>
            )}
            {canUpload && Object.keys(uploadProgress).length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h3>Upload Progress</h3>
                {Object.entries(uploadProgress).map(([fileName, progress]) => (
                  <div key={fileName}>
                    <span>
                      {fileName}: {progress}%
                    </span>
                    <div
                      style={{
                        width: '100%',
                        backgroundColor: '#f0f0f0',
                        marginTop: '5px',
                      }}
                    >
                      <div
                        style={{
                          width: `${progress}% `,
                          backgroundColor: '#1890ff',
                          height: '5px',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Row>
          {hasSelected && selectedRowKeys.length < resumes.length && (
            <Row>
              <Col sm={24} style={{ textAlign: 'right' }}>
                <Button className="dyn-btn-primary ml-2 mr-2" onClick={onSelectAllChange}>
                  {languageContext.t('Select All')} {resumes.length} {languageContext.t('resumes')}
                </Button>
              </Col>
            </Row>
          )}
          <div
            ref={containerRef}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{ position: 'relative' }}
          >
            <Table
              dataSource={resumes}
              columns={columns}
              className="dyn-table"
              rowSelection={agent.active ? rowSelection : null}
              expandable={{
                expandedRowRender: record => (
                  <>
                    {/* <Button className="dyn-btn-secondary ml-2 mr-2" type="text" onClick={() => downloadSummary(record)}>{languageContext.t('Download Summary')}</Button> */}
                    {Object.entries(record.details).map(([keyName, keyValue]) => (
                      <>
                        <b>{languageContext.t(keyName)}</b>
                        {typeof keyValue === 'string' && (keyValue.startsWith('https://') || keyValue.startsWith('http://')) ? (
                          <a href={keyValue} target="_blank" rel="noopener noreferrer">
                            {keyValue}
                          </a>
                        ) : (
                          String(keyValue)
                        )}
                      </>
                    ))}
                  </>
                ),
                rowExpandable: record => record.status === 2 && record.details,
                expandRowByClick: true,
              }}
            />
            {isDragging && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                }}
              >
                <div style={{ textAlign: 'center', color: 'white' }}>
                  <CloudUploadOutlined style={{ fontSize: '48px' }} />
                  <h2>Drop files here to upload</h2>
                </div>
              </div>
            )}
          </div>
        </Content>
      </Tabs.TabPane>
    </Tabs>
  )
}

export default AgentRecruiterDetails
